/**
 * admin_dashboard_controller.js — Admin dashboard (Stimulus).
 *
 * Ports the former static/js/admin_dashboard.js
 * (attachOnce('adminDashboardListeners') + turbo:load) to an element-scoped
 * controller. The page renders a growth chart (TurboChart), timeline/mode
 * buttons, an activity-feed exclude-demo toggle, an auto-refresh interval, and
 * an activity feed. The growth chart always breaks demo accounts out onto their
 * own line (no toggle). Reads seed data from the #admin-dashboard-data JSON
 * script element.
 *
 * Lifecycle (replaces attachOnce + turbo:load):
 *   connect() fills the active-user card, mounts the chart, and binds the
 *   control listeners + the 5-minute auto-refresh interval; disconnect()
 *   clears the interval (the bound element
 *   listeners on the buttons/toggles die with the swapped <body>, and the chart
 *   is owned by TurboChart's destroy-before-recreate + before-cache teardown).
 *   connect() fires per element on each Turbo navigation, so the chart and the
 *   listeners always attach to the freshly-rendered DOM — no once-guard needed.
 *
 * Chart.js is loaded through _load_script.js from the chartSrc value, and init
 * waits for it: a body vendor script loads asynchronously on a Turbo visit,
 * after turbo:load has fired.
 *
 * The page-action functions (updateGrowthChart, refreshDashboard,
 * refreshActivity, dismissAlert, runSystemMaintenance, etc.) stay as module
 * functions; dismissAlert + runSystemMaintenance remain exposed on window for
 * the data-action="call-fn" dispatcher.
 *
 * data-controller="admin-dashboard" hosts on the page's container-fluid.
 * Template: templates/admin/dashboard.html
 */

import { Controller } from "@hotwired/stimulus";
import { loadScript } from "./_load_script.js";

    // Growth chart state (chart instance itself lives in TurboChart registry — SSOT).
    // Which series exist, and how each is labelled and coloured, is owned by the
    // server (AdminService.GROWTH_SERIES) and seeded into #admin-dashboard-data.
    var currentDays = 30;
    var currentMode = 'daily';
    var seriesMeta = [];

    var TIME_LABELS = {
        30: 'Last 30 Days',
        90: 'Last 90 Days',
        180: 'Last 180 Days',
        365: 'Last Year',
        0: 'All Time'
    };

    /** Series keys the admin has ticked, in menu order. */
    function selectedMetrics() {
        return Array.prototype.slice.call(document.querySelectorAll('[data-metric]'))
            .filter(function (input) { return input.checked; })
            .map(function (input) { return input.dataset.metric; });
    }

    /** Chart.js datasets for whichever series the payload actually carries.
     *
     * A requested series can be missing (its source failed); it is then simply
     * not plotted, rather than drawn as a misleading zero line.
     */
    function buildDatasets(series) {
        return seriesMeta
            .filter(function (meta) { return Object.prototype.hasOwnProperty.call(series, meta.key); })
            .map(function (meta) {
                var cumulative = currentMode === 'cumulative' && meta.cumulative_label;
                return {
                    label: cumulative ? meta.cumulative_label : meta.label,
                    data: series[meta.key],
                    borderColor: meta.color,
                    backgroundColor: meta.color.replace('rgb(', 'rgba(').replace(')', ', 0.1)'),
                    borderDash: meta.dashed ? [5, 5] : undefined,
                    tension: 0.4
                };
            });
    }

    function updateGrowthChart() {
        var loading = document.getElementById('chartLoading');
        var chartTitle = document.getElementById('chartTitle');
        var chart = TurboChart.get('growthChart');
        var metrics = selectedMetrics();

        chartTitle.textContent = 'Growth Metrics (' + TIME_LABELS[currentDays] + ')';

        // Everything unticked is a deliberate empty chart, not a reason to fetch
        // (the API would fall back to its default selection).
        if (!metrics.length) {
            if (chart) {
                chart.data.datasets = [];
                chart.update();
            }
            return;
        }

        var params = new URLSearchParams({ days: currentDays, mode: currentMode });
        metrics.forEach(function (key) { params.append('metrics', key); });

        loading.classList.remove('d-none');

        fetch('/admin/api/growth-chart?' + params.toString())
            .then(function (response) { return response.json(); })
            .then(function (data) {
                if (!data.success || !chart) {
                    showNotification('Failed to load chart data', 'error');
                    return;
                }
                chart.data.labels = data.labels;
                chart.data.datasets = buildDatasets(data.series);
                chart.update();

                if (data.warning) showNotification(data.warning, 'warning');
            })
            .catch(function (error) {
                console.error('Error loading chart data:', error);
                showNotification('Error loading chart data', 'error');
            })
            .finally(function () {
                loading.classList.add('d-none');
            });
    }

    /** Fill the "Active Users (24h)" card, which the server renders empty.
     *
     * The count is a PostHog query, so it is fetched after paint rather than
     * blocking the dashboard render on a third party. On any failure the card
     * says so and keeps its dash: a zero here would read as "nobody used
     * Chippie today", which is the exact misreading this card was fixed for.
     */
    function loadActiveUsers() {
        var countEl = document.getElementById('activeUsersCount');
        var shareEl = document.getElementById('activeUsersShare');
        if (!countEl || !shareEl) return;

        fetch('/admin/api/active-users')
            .then(function (response) { return response.json(); })
            .then(function (data) {
                if (data.count === null || data.count === undefined) {
                    shareEl.textContent = data.warning || 'Unavailable';
                    return;
                }
                countEl.textContent = data.count;
                shareEl.textContent = data.percentage === null
                    ? 'No users yet'
                    : data.percentage + '% of total';
            })
            .catch(function (error) {
                console.error('Error loading active users:', error);
                shareEl.textContent = 'Unavailable';
            });
    }

    function refreshDashboard() {
        window.chippiePageCheck.refresh();
    }

    function refreshActivity() {
        var refreshBtn = document.querySelector('[data-fn="refreshActivity"]');
        var spinner = refreshBtn.querySelector('i');

        // Show loading state
        spinner.className = 'bi bi-arrow-clockwise fa-spin';
        refreshBtn.disabled = true;

        var activityExcludeDemo = false;
        var toggle = document.getElementById('activityExcludeDemoToggle');
        if (toggle) activityExcludeDemo = toggle.checked;
        fetch('/admin/api/recent-activity?exclude_demo=' + activityExcludeDemo)
            .then(function (response) { return response.json(); })
            .then(function (data) {
                if (data.success) {
                    updateActivityList(data.activities);
                    document.getElementById('activityCount').textContent = data.activities.length;
                    showNotification('Activity refreshed successfully', 'success');
                } else {
                    showNotification('Failed to refresh activity', 'error');
                }
            })
            .catch(function (error) {
                console.error('Error refreshing activity:', error);
                showNotification('Error refreshing activity', 'error');
            })
            .finally(function () {
                // Reset button state
                spinner.className = 'bi bi-arrow-clockwise';
                refreshBtn.disabled = false;
            });
    }

    function updateActivityList(activities) {
        var listElement = document.getElementById('recentActivityList');

        if (activities.length === 0) {
            listElement.innerHTML =
                '<div class="list-group-item text-center py-4">' +
                    '<i class="bi bi-clock-history text-muted" style="font-size: 2rem;"></i>' +
                    '<p class="text-muted mt-2 mb-0">No recent activity</p>' +
                    '<small class="text-muted">Activity will appear here as users interact with the system</small>' +
                '</div>';
            return;
        }

        var activityHtml = activities.map(function (activity) {
            var icon = getActivityIcon(activity.action);
            var user = activity.user ? activity.user.display_name : 'System';
            var business = activity.business ? ' - ' + activity.business.name : '';
            var time = new Date(activity.created_at).toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit'});
            var date = new Date(activity.created_at).toLocaleDateString('en-GB', {day: '2-digit', month: '2-digit'});

            return '<div class="list-group-item d-flex justify-content-between align-items-start activity-item">' +
                    '<div class="d-flex">' +
                        '<div class="me-3">' + icon + '</div>' +
                        '<div class="me-auto">' +
                            '<div class="fw-bold">' + activity.action + '</div>' +
                            '<small class="text-muted">' + user + business + '</small>' +
                        '</div>' +
                    '</div>' +
                    '<div class="text-end">' +
                        '<small class="text-muted">' + time + '</small>' +
                        '<br>' +
                        '<small class="text-muted">' + date + '</small>' +
                    '</div>' +
                '</div>';
        }).join('');

        listElement.innerHTML = activityHtml;
    }

    function getActivityIcon(action) {
        var actionLower = action.toLowerCase();
        if (actionLower.includes('login')) {
            return '<i class="bi bi-box-arrow-in-right text-success"></i>';
        } else if (actionLower.includes('logout')) {
            return '<i class="bi bi-box-arrow-right text-secondary"></i>';
        } else if (actionLower.includes('create')) {
            return '<i class="bi bi-plus-circle text-primary"></i>';
        } else if (actionLower.includes('update') || actionLower.includes('edit')) {
            return '<i class="bi bi-pencil text-warning"></i>';
        } else if (actionLower.includes('delete')) {
            return '<i class="bi bi-trash text-danger"></i>';
        } else {
            return '<i class="bi bi-activity text-info"></i>';
        }
    }

    function dismissAlert(alertId) {
        fetch('/api/admin/alerts/dismiss', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ alert_id: alertId })
        })
        .then(function (response) { return response.json(); })
        .then(function (data) {
            if (data.success) {
                window.chippiePageCheck.refresh();
            } else {
                alert('Failed to dismiss alert: ' + (data.message || 'Unknown error'));
            }
        })
        .catch(function (error) {
            console.error('Error:', error);
            alert('An error occurred while dismissing the alert');
        });
    }

    function runSystemMaintenance() {
        var modal = new bootstrap.Modal(document.getElementById('maintenanceModal'));
        modal.show();

        // Use .onclick to replace (not stack) the handler on repeated calls
        var startBtn = document.getElementById('startMaintenance');
        if (!startBtn) return;

        startBtn.onclick = function () {
            var tasks = [];
            if (document.getElementById('cleanupLogs').checked) tasks.push('cleanup_logs');
            if (document.getElementById('optimizeDatabase').checked) tasks.push('optimize_database');
            if (document.getElementById('clearTempFiles').checked) tasks.push('clear_temp_files');
            if (document.getElementById('updateStatistics').checked) tasks.push('update_statistics');

            if (tasks.length === 0) {
                alert('Please select at least one maintenance task');
                return;
            }

            var btn = startBtn;
            var spinner = btn.querySelector('.spinner-border');

            btn.disabled = true;
            if (spinner) spinner.classList.remove('d-none');
            btn.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Running Maintenance...';

            fetch('/api/admin/maintenance', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ tasks: tasks })
            })
            .then(function (response) { return response.json(); })
            .then(function (data) {
                if (data.success) {
                    alert('Maintenance completed successfully');
                    modal.hide();
                    window.chippiePageCheck.refresh();
                } else {
                    alert('Maintenance failed: ' + (data.message || 'Unknown error'));
                }
            })
            .catch(function (error) {
                console.error('Error:', error);
                alert('An error occurred during maintenance');
            })
            .finally(function () {
                btn.disabled = false;
                if (spinner) spinner.classList.add('d-none');
                btn.innerHTML = 'Start Maintenance';
            });
        };
    }

    // Expose for template dispatcher (data-action="call-fn" in global-actions.js).
    window.dismissAlert = dismissAlert;
    window.runSystemMaintenance = runSystemMaintenance;

export default class extends Controller {
        static values = { chartSrc: String };

        connect() {
            // Independent of Chart.js and of the chart element, so it runs
            // before the deferral below rather than inside _init().
            loadActiveUsers();

            loadScript(this.chartSrcValue, 'Chart')
                .then(() => { if (this.element.isConnected) this._init(); })
                .catch(function (error) { console.error('Growth chart not drawn:', error); });
        }

        disconnect() {
            if (this._refreshInterval) {
                clearInterval(this._refreshInterval);
                this._refreshInterval = null;
            }
        }

        _init() {
            // Initialize growth chart - only if element exists (this page only)
            var chartElement = document.getElementById('growthChart');
            if (!chartElement) return;

            var dataEl = document.getElementById('admin-dashboard-data');
            var data = JSON.parse(dataEl.textContent);
            seriesMeta = data.seriesMeta;

            // Module state outlives a Turbo navigation, but the seed the server
            // just rendered is always the default 30-day daily window with the
            // default series ticked. Resync, or the legend labels the seeded
            // daily data as cumulative totals.
            currentDays = 30;
            currentMode = 'daily';

            TurboChart.mount('growthChart', {
                type: 'line',
                data: {
                    labels: data.chartLabels,
                    datasets: buildDatasets(data.chartSeries)
                },
                options: {
                    responsive: true,
                    plugins: {
                        legend: {
                            position: 'top',
                        },
                        title: {
                            display: false
                        }
                    },
                    scales: {
                        y: {
                            beginAtZero: true
                        }
                    }
                }
            });

            // Metric checkbox handlers
            document.querySelectorAll('[data-metric]').forEach(function (input) {
                input.addEventListener('change', function () {
                    updateGrowthChart();
                });
            });

            // Timeline button handlers
            document.querySelectorAll('[data-days]').forEach(function (btn) {
                btn.addEventListener('click', function () {
                    document.querySelectorAll('[data-days]').forEach(function (b) { b.classList.remove('active'); });
                    this.classList.add('active');
                    currentDays = parseInt(this.dataset.days);
                    updateGrowthChart();
                });
            });

            // Mode button handlers
            document.querySelectorAll('[data-mode]').forEach(function (btn) {
                btn.addEventListener('click', function () {
                    document.querySelectorAll('[data-mode]').forEach(function (b) { b.classList.remove('active'); });
                    this.classList.add('active');
                    currentMode = this.dataset.mode;
                    updateGrowthChart();
                });
            });

            // Activity exclude demo toggle handler
            var activityExcludeDemoToggle = document.getElementById('activityExcludeDemoToggle');
            if (activityExcludeDemoToggle) {
                activityExcludeDemoToggle.addEventListener('change', function () {
                    refreshActivity();
                });
            }

            // Auto-refresh dashboard every 5 minutes
            this._refreshInterval = setInterval(refreshDashboard, 300000);
        }
}
